/**
 * Reusable Export-to-XLSX column definitions for firm-shaped rows (used on
 * Dashboard, New Customers, Renewals, Enterprise, Follow-ups, Power Users
 * and Admin → Firms). Every page can pick the subset it wants.
 *
 * format() helpers return primitive types so Excel can sum/sort correctly.
 */

const safeStr = (v) => (v == null ? "" : String(v));

export const FIRM_BASE_COLUMNS = [
  { key: "firm_name", label: "Firm name" },
  { key: "subdomain", label: "Subdomain" },
  { key: "num_users", label: "Users" },
  { key: "tier", label: "Tier" },
  { key: "poc", label: "POC", format: (v) => safeStr(v) },
  { key: "city", label: "City" },
  { key: "status", label: "Status" },
  { key: "activation_date", label: "Activation date" },
  { key: "risk", label: "Risk" },
  { key: "last_seen", label: "Last seen (UTC)" },
  { key: "days_since_seen", label: "Days since last seen" },
  { key: "events_30d", label: "Events (30d)" },
  { key: "subscription_end_date", label: "Subscription ends" },
];

export const CUSTOMER_NEW_COLUMNS = [
  { key: "firm_name", label: "Firm name" },
  { key: "subdomain", label: "Subdomain" },
  { key: "num_users", label: "Users" },
  { key: "tier", label: "Tier" },
  { key: "poc", label: "POC" },
  { key: "city", label: "City" },
  { key: "segment", label: "Segment" },
  { key: "bucket", label: "Bucket" },
  { key: "health_score", label: "Health score" },
  { key: "day", label: "Day of journey" },
  { key: "activation_date", label: "Activation date" },
  { key: "events_30d", label: "Events (30d)" },
  { key: "last_seen", label: "Last seen (UTC)" },
];

export const RENEWAL_COLUMNS = [
  { key: "firm_name", label: "Firm name" },
  { key: "subdomain", label: "Subdomain" },
  { key: "num_users", label: "Users" },
  { key: "tier", label: "Tier" },
  { key: "risk", label: "Risk" },
  { key: "poc", label: "POC" },
  { key: "city", label: "City" },
  { key: "subscription_end_date", label: "Subscription ends" },
  { key: "days_to_renewal", label: "Days to renewal" },
  { key: "bucket", label: "Bucket" },
  { key: "activation_date", label: "Activation date" },
  { key: "events_30d", label: "Events (30d)" },
  { key: "last_seen", label: "Last seen (UTC)" },
];

export const ENTERPRISE_COLUMNS = [
  { key: "firm_name", label: "Firm name" },
  { key: "subdomain", label: "Subdomain" },
  { key: "num_users", label: "Users" },
  { key: "poc", label: "POC" },
  { key: "city", label: "City" },
  { key: "bucket", label: "Bucket" },
  { key: "activation_date", label: "Activation date" },
  { key: "events_30d", label: "Events (30d)" },
  { key: "last_seen", label: "Last seen (UTC)" },
  { key: "days_since_seen", label: "Days since last seen" },
  { key: "subscription_end_date", label: "Subscription ends" },
];

export const POWER_USER_COLUMNS = [
  { key: "firm_name", label: "Firm name" },
  { key: "subdomain", label: "Subdomain" },
  { key: "num_users", label: "Users" },
  { key: "tier", label: "Tier" },
  { key: "poc", label: "POC" },
  { key: "city", label: "City" },
  { key: "events_30d", label: "Events (30d)" },
  { key: "events_90d", label: "Events (90d)" },
  { key: "import_books", label: "Import Books" },
  { key: "ledger_classification", label: "Ledger Classification" },
  { key: "statement_generation", label: "Statement Generation" },
  { key: "ledger_scrutiny", label: "Ledger Scrutiny" },
  { key: "total_clients", label: "Total Clients" },
  { key: "total_assignments", label: "Total Assignments" },
  { key: "last_seen", label: "Last seen (UTC)" },
];

export const FOLLOWUP_COLUMNS = [
  { key: "firm_name", label: "Firm name" },
  { key: "subdomain", label: "Subdomain" },
  { key: "num_users", label: "Users" },
  { key: "tier", label: "Tier" },
  { key: "poc", label: "POC" },
  { key: "city", label: "City" },
  { key: "risk", label: "Risk" },
  { key: "last_seen", label: "Last seen (UTC)" },
  { key: "days_since_seen", label: "Days since last seen" },
  { key: "events_30d", label: "Events (30d)" },
];

export const CALL_LOG_COLUMNS = [
  { key: "called_at", label: "Called at (UTC)" },
  { key: "firm_name", label: "Firm name" },
  { key: "firm_subdomain", label: "Subdomain" },
  { key: "poc_name", label: "POC" },
  { key: "outcome", label: "Outcome" },
  { key: "duration_minutes", label: "Duration (min)", format: (v) => (v == null ? "" : Number(v)) },
  { key: "remark", label: "Remark" },
  { key: "logged_by_email", label: "Logged by" },
  { key: "created_at", label: "Created at (UTC)" },
];

export const ACTIVITY_COLUMNS = [
  { key: "type_label", label: "Type" },
  { key: "occurred_at", label: "Occurred at (UTC)" },
  { key: "firm_name", label: "Firm name" },
  { key: "firm_subdomain", label: "Subdomain" },
  { key: "firm_tier", label: "Tier" },
  { key: "firm_poc", label: "POC" },
  { key: "duration_minutes", label: "Duration (min)", format: (v) => (v == null ? "" : Number(v)) },
  { key: "remark", label: "Remark" },
  { key: "logged_by_name", label: "Logged by" },
  { key: "logged_by_role", label: "Role" },
  { key: "logged_by_email", label: "Email" },
  { key: "created_at", label: "Created at (UTC)" },
];

export const USER_COLUMNS = [
  { key: "email", label: "Email" },
  { key: "name", label: "Name" },
  { key: "role", label: "Role" },
  { key: "poc_name", label: "POC name" },
  { key: "last_login_at", label: "Last login (UTC)" },
  { key: "created_at", label: "Created at (UTC)" },
];
